import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2, ChevronLeft } from 'lucide-react';
import { useGatePass, useCreateGatePass, useUpdateGatePass } from '../../hooks/useGatePasses.js';
import { locationHooks, meHooks, ppcHooks, customerHooks, portHooks, supplierHooks, purchaseCenterHooks } from '../../hooks/useMasters.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { api } from '../../utils/api.js';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Card from '../../components/ui/Card.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { GP_TYPE_LABELS, GP_SUBTYPE_LABELS, FREEZING_TYPES, ICE_QTY_UNITS, ICE_USAGE_TYPES, FORMS, BY_HAND_ALLOWED_TYPES } from '../../utils/constants.js';

// ─── Schemas ─────────────────────────────────────────────────────────────────
const itemSchema = z.object({
  // RM fields
  species: z.string().optional(),
  grade: z.string().optional(),
  form: z.string().optional(),
  quantity: z.coerce.number().optional(),
  boxes: z.coerce.number().int().optional(),
  lotNumber: z.string().optional(),
  lotSuffix: z.string().optional(),
  batchNumber: z.string().optional(),
  temperature: z.coerce.number().optional(),
  supplierId: z.string().optional(),
  purchaseCenterId: z.string().optional(),
  // FP fields
  productName: z.string().optional(),
  size: z.string().optional(),
  packSize: z.string().optional(),
  freezingType: z.string().optional(),
  packing: z.string().optional(),
  brand: z.boolean().optional(),
  brandName: z.string().optional(),
  cartons: z.coerce.number().int().optional(),
  netWeight: z.coerce.number().optional(),
  grossWeight: z.coerce.number().optional(),
  // RET / general fields
  itemDescription: z.string().optional(),
  description: z.string().optional(),
  serialNumber: z.string().optional(),
  quantity2: z.coerce.number().optional(),
  unit: z.string().optional(),
});

const schema = z.object({
  type: z.string().min(1, 'Required'),
  subType: z.string().optional(),
  ownerType: z.enum(['SREERAGAM', 'MERCHANT_EXPORTER']),
  merchantExporterId: z.string().optional(),
  sourceLocationId: z.string().min(1, 'Required'),
  // Destination
  destinationLocationId: z.string().optional(),
  destinationPPCId: z.string().optional(),
  destinationText: z.string().optional(),
  // Vehicle
  vehicleNumber: z.string().optional(),
  byHand: z.boolean().optional(),
  driverName: z.string().optional(),
  driverContact: z.string().optional(),
  remarks: z.string().optional(),
  // Expected return
  hasExpectedReturn: z.boolean().optional(),
  expectedReturnDate: z.string().optional(),
  // Export FP
  freightForwarder: z.string().optional(),
  shippingLine: z.string().optional(),
  vesselName: z.string().optional(),
  containerNumber: z.string().optional(),
  blReference: z.string().optional(),
  invoicePlRef: z.string().optional(),
  portId: z.string().optional(),
  invoiceNo: z.string().optional(),
  buyerName: z.string().optional(),
  exportCountry: z.string().optional(),
  // Ice
  iceUsageType: z.string().optional(),
  iceCustomerId: z.string().optional(),
  iceDestLocationId: z.string().optional(),
  iceQtyUnit: z.string().optional(),
  items: z.array(itemSchema).optional(),
});

const SUBTYPES_BY_TYPE = {
  OUT_RM: ['TO_OWN_FACTORY', 'TO_THIRD_PARTY_PPC'],
  OUT_FP: ['EXPORT_SHIPMENT', 'FACTORY_TO_FACTORY', 'TO_MERCHANT_EXPORTER'],
  IN_RM:  ['FROM_SUPPLIER', 'FROM_OWN_FACTORY', 'FROM_THIRD_PARTY_PPC'],
  IN_FP:  ['FROM_OWN_FACTORY', 'FROM_THIRD_PARTY_PPC'],
  RET:    ['FOR_REPAIR', 'INTER_FACTORY_LOAN', 'TO_MERCHANT_EXPORTER'],
};

// ─── Inline customer creator ─────────────────────────────────────────────────
function InlineCustomerModal({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/masters/customers', { name: name.trim() });
      onCreate(data.data);
      setName('');
    } finally {
      setSaving(false);
    }
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title="Add New Customer">
      <div className="space-y-4">
        <Input label="Customer Name" value={name} onChange={e => setName(e.target.value)} placeholder="Enter customer name" />
        <div className="flex justify-end gap-3">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} loading={saving}>Add Customer</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Form ────────────────────────────────────────────────────────────────
export default function GatePassFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;

  const { data: existing } = useGatePass(isEdit ? id : null);
  const { data: locations = [] } = locationHooks.useList();
  const { data: mes = [] } = meHooks.useList();
  const { data: ppcs = [] } = ppcHooks.useList();
  const { data: customers = [], refetch: refetchCustomers } = customerHooks.useList();
  const { data: ports = [] } = portHooks.useList();
  const { data: suppliers = [] } = supplierHooks.useList();
  const { data: purchaseCenters = [] } = purchaseCenterHooks.useList();

  const createMut = useCreateGatePass();
  const updateMut = useUpdateGatePass(id);

  const [inlineCustomerOpen, setInlineCustomerOpen] = useState(false);
  const [extraCustomers, setExtraCustomers] = useState([]);

  const {
    register, handleSubmit, watch, reset, control, setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { ownerType: 'SREERAGAM', byHand: false, items: [{}] },
  });

  const { fields: itemFields, append, remove } = useFieldArray({ control, name: 'items' });

  const watchType     = watch('type');
  const watchOwner    = watch('ownerType');
  const watchSubType  = watch('subType');
  const watchByHand   = watch('byHand');
  const watchIceUsage = watch('iceUsageType');
  const watchDestType = watch('_destType'); // internal selector
  const watchItems    = watch('items') || [];

  useEffect(() => {
    if (existing) {
      reset({
        ...existing,
        byHand: existing.byHand || false,
        expectedReturnDate: existing.expectedReturnDate?.slice(0, 10) || '',
        items: existing.items?.length ? existing.items : [{}],
        _destType: existing.destinationLocationId ? 'location'
          : existing.destinationPPCId ? 'ppc'
          : existing.destinationText ? 'other' : '',
      });
    }
  }, [existing, reset]);

  useEffect(() => {
    if (!isEdit && user?.locations?.length) {
      reset(v => ({ ...v, sourceLocationId: user.locations[0].locationId }));
    }
  }, [user, isEdit]);

  const allCustomers = [...customers, ...extraCustomers];

  const handleInlineCustomerCreate = (newCustomer) => {
    setExtraCustomers(prev => [...prev, newCustomer]);
    setValue('iceCustomerId', newCustomer.id);
    refetchCustomers();
  };

  const onSubmit = async (values) => {
    const { _destType, ...payload } = values;
    // Clear destination fields not relevant to selection
    if (_destType === 'location')  { payload.destinationPPCId = null; payload.destinationText = null; }
    if (_destType === 'ppc')       { payload.destinationLocationId = null; payload.destinationText = null; }
    if (_destType === 'other')     { payload.destinationLocationId = null; payload.destinationPPCId = null; }
    if (!_destType)                { payload.destinationLocationId = null; payload.destinationPPCId = null; payload.destinationText = null; }

    payload.items = (values.items || []).filter(i =>
      i.species || i.productName || i.itemDescription || i.description || i.quantity || i.quantity2
    );

    const result = isEdit
      ? await updateMut.mutateAsync(payload)
      : await createMut.mutateAsync(payload);
    navigate(`/gate-passes/${result.id || id}`);
  };

  // Derived flags
  const isRM      = ['OUT_RM', 'IN_RM'].includes(watchType);
  const isInRM    = watchType === 'IN_RM';
  const isOutRM   = watchType === 'OUT_RM';
  const isFP      = ['OUT_FP', 'IN_FP'].includes(watchType);
  const isOutFP   = watchType === 'OUT_FP';
  const isRET     = watchType === 'RET';
  const isInward  = ['IN_RM', 'IN_FP', 'IN_ICE', 'IN_FC', 'IN_GEN'].includes(watchType);
  const isOutward = ['OUT_RM', 'OUT_FP', 'OUT_ICE', 'OUT_FC', 'OUT_GEN'].includes(watchType);
  const isIce     = ['OUT_ICE', 'IN_ICE'].includes(watchType);
  const isFC      = ['OUT_FC', 'IN_FC'].includes(watchType);
  const isGen     = ['OUT_GEN', 'IN_GEN'].includes(watchType);
  const isGeneral = isFC || isGen;
  const isExportShipment = isOutFP && watchSubType === 'EXPORT_SHIPMENT';
  const showPPC   = watchSubType === 'TO_THIRD_PARTY_PPC' || watchSubType === 'FROM_THIRD_PARTY_PPC';
  const byHandAllowed = BY_HAND_ALLOWED_TYPES.includes(watchType);

  // Compute lot number preview for IN_RM
  const lotPreviewText = isInRM && existing?.baseLotNumber ? existing.baseLotNumber : null;

  const inputCls = 'w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400';
  const selectCls = 'rounded border border-gray-200 px-2 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-400';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <Link to="/gate-passes" className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-5 h-5" /></Link>
        <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Gate Pass' : 'New Gate Pass'}</h1>
      </div>

      {/* ── Type ──────────────────────────────────────────────────── */}
      <Card title="Gate Pass Type">
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select label="Type" required error={errors.type?.message} {...register('type')}>
            <option value="">Select type…</option>
            {Object.entries(GP_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          {watchType && SUBTYPES_BY_TYPE[watchType] && (
            <Select label="Sub-type" {...register('subType')}>
              <option value="">Select sub-type…</option>
              {SUBTYPES_BY_TYPE[watchType].map(s => <option key={s} value={s}>{GP_SUBTYPE_LABELS[s]}</option>)}
            </Select>
          )}
        </div>
      </Card>

      {/* ── Location & Ownership ───────────────────────────────────── */}
      <Card title="Location & Ownership">
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select label="Source Location" required error={errors.sourceLocationId?.message} {...register('sourceLocationId')}>
            <option value="">Select location…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </Select>
          <Select label="Owner" required {...register('ownerType')}>
            <option value="SREERAGAM">Sreeragam Exports Pvt. Ltd.</option>
            <option value="MERCHANT_EXPORTER">Merchant Exporter</option>
          </Select>
          {watchOwner === 'MERCHANT_EXPORTER' && (
            <Select label="Merchant Exporter" required {...register('merchantExporterId')}>
              <option value="">Select merchant exporter…</option>
              {mes.map(me => <option key={me.id} value={me.id}>{me.name}</option>)}
            </Select>
          )}
        </div>
      </Card>

      {/* ── Destination ───────────────────────────────────────────── */}
      {(isOutward || isRET || isFC || isGen) && !isIce && (
        <Card title="Destination">
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {showPPC ? (
              <Select label="Third-Party PPC" {...register('destinationPPCId')}>
                <option value="">Select PPC…</option>
                {ppcs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            ) : (
              <>
                <Select label="Destination Type" {...register('_destType')}>
                  <option value="">Select…</option>
                  <option value="location">Own Location</option>
                  <option value="ppc">Third-Party PPC</option>
                  <option value="other">Other (specify)</option>
                </Select>
                {watchDestType === 'location' && (
                  <Select label="Destination Location" {...register('destinationLocationId')}>
                    <option value="">Select location…</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
                  </Select>
                )}
                {watchDestType === 'ppc' && (
                  <Select label="Third-Party PPC" {...register('destinationPPCId')}>
                    <option value="">Select PPC…</option>
                    {ppcs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                )}
                {watchDestType === 'other' && (
                  <Input label="Destination (specify)" placeholder="Enter destination name or address" {...register('destinationText')} />
                )}
              </>
            )}
          </div>
        </Card>
      )}

      {/* ── Ice-specific section ──────────────────────────────────── */}
      {isIce && (
        <Card title="Ice Details">
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Usage Type" required {...register('iceUsageType')}>
              <option value="">Select…</option>
              {ICE_USAGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <Select label="Quantity Unit" required {...register('iceQtyUnit')}>
              <option value="">Select unit…</option>
              {ICE_QTY_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </Select>
            {watchIceUsage === 'OWN_CONSUMPTION' && (
              <Select label="Destination Location (Own)" required {...register('iceDestLocationId')}>
                <option value="">Select location…</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
              </Select>
            )}
            {watchIceUsage === 'SALES' && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Customer <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <select className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white" {...register('iceCustomerId')}>
                    <option value="">Select customer…</option>
                    {allCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <Button type="button" variant="secondary" size="sm" icon={Plus} onClick={() => setInlineCustomerOpen(true)}>New</Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ── Vehicle & Carrier ────────────────────────────────────── */}
      <Card title="Vehicle & Carrier">
        <div className="p-6 space-y-4">
          {byHandAllowed && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" {...register('byHand')} className="rounded" />
              <span className="font-medium">By Hand</span>
              <span className="text-gray-500">(no vehicle — hand carry only)</span>
            </label>
          )}
          {!watchByHand && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label={<>Vehicle Number <span className="text-red-500">*</span></>}
                placeholder="e.g. KL01AB1234"
                {...register('vehicleNumber')}
                error={errors.vehicleNumber?.message}
              />
              <Input label="Driver Name" {...register('driverName')} />
              <Input label="Driver Contact" {...register('driverContact')} />
            </div>
          )}
          {watchByHand && (
            <p className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">By Hand selected — vehicle number not required.</p>
          )}
        </div>
      </Card>

      {/* ── Export Shipment Details ───────────────────────────────── */}
      {isExportShipment && (
        <Card title="Export Shipment Details">
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Port of Shipment *" {...register('portId')}>
              <option value="">Select port…</option>
              {ports.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
            </Select>
            <Input label="Invoice No. *" placeholder="Invoice number" {...register('invoiceNo')} />
            <Input label="Buyer Name *" placeholder="Name of the buyer" {...register('buyerName')} />
            <Input label="Export Country *" placeholder="Country of export" {...register('exportCountry')} />
            <Input label="Freight Forwarder" {...register('freightForwarder')} />
            <Input label="Shipping Line" {...register('shippingLine')} />
            <Input label="Vessel Name" {...register('vesselName')} />
            <Input label="Container Number" {...register('containerNumber')} />
            <Input label="BL Reference" {...register('blReference')} />
            <Input label="Invoice / PL Reference" {...register('invoicePlRef')} />
          </div>
        </Card>
      )}

      {/* ── Non-export FP shipping info ──────────────────────────── */}
      {isOutFP && !isExportShipment && (
        <Card title="Shipment Details">
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Freight Forwarder" {...register('freightForwarder')} />
            <Input label="Shipping Line" {...register('shippingLine')} />
            <Input label="Vessel Name" {...register('vesselName')} />
            <Input label="Container Number" {...register('containerNumber')} />
            <Input label="BL Reference" {...register('blReference')} />
            <Input label="Invoice / PL Reference" {...register('invoicePlRef')} />
          </div>
        </Card>
      )}

      {/* ── Expected Return ───────────────────────────────────────── */}
      {(isOutRM || isRET) && (
        <Card title="Expected Return">
          <div className="p-6 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" {...register('hasExpectedReturn')} className="rounded" />
              <span>Material expected to return</span>
            </label>
            {watch('hasExpectedReturn') && (
              <Input label="Expected Return Date" type="date" {...register('expectedReturnDate')} />
            )}
          </div>
        </Card>
      )}

      {/* ── IN_RM Lot Number ──────────────────────────────────────── */}
      {isInRM && lotPreviewText && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <span className="text-sm text-teal-700 font-medium">Base Lot Number:</span>
          <span className="font-mono font-bold text-teal-900 text-base">{lotPreviewText}</span>
          <span className="text-xs text-teal-600 ml-2">(auto-generated; suffixes A, B, C… added per supplier/purchase center)</span>
        </div>
      )}
      {isInRM && !lotPreviewText && !isEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
          Lot number will be auto-generated when the gate pass is created based on the F Code configured for this location.
        </div>
      )}

      {/* ── Items Table ───────────────────────────────────────────── */}
      <Card title="Items"
        action={<Button type="button" variant="ghost" size="sm" icon={Plus} onClick={() => append({})}>Add Row</Button>}>
        <div className="overflow-x-auto">
          {isIce ? (
            // Ice items
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Description', 'Quantity', 'Unit', ''].map(h => <th key={h} className="text-left px-3 py-2 text-xs font-medium text-gray-600">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {itemFields.map((field, idx) => (
                  <tr key={field.id}>
                    <td className="px-2 py-1"><input className={inputCls} placeholder="Ice description" {...register(`items.${idx}.description`)} /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" className={`w-24 ${inputCls}`} {...register(`items.${idx}.quantity2`)} /></td>
                    <td className="px-2 py-1">
                      <select className={selectCls} {...register(`items.${idx}.unit`)}>
                        <option value="">Unit</option>
                        {ICE_QTY_UNITS.map(u => <option key={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1">{itemFields.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : isGeneral ? (
            // FC / General items
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Description', 'Quantity', 'Unit', ''].map(h => <th key={h} className="text-left px-3 py-2 text-xs font-medium text-gray-600">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {itemFields.map((field, idx) => (
                  <tr key={field.id}>
                    <td className="px-2 py-1"><input className={inputCls} placeholder="Item description" {...register(`items.${idx}.description`)} /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" className={`w-24 ${inputCls}`} {...register(`items.${idx}.quantity`)} /></td>
                    <td className="px-2 py-1"><input className={`w-24 ${inputCls}`} placeholder="Unit" {...register(`items.${idx}.unit`)} /></td>
                    <td className="px-2 py-1">{itemFields.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : isRET ? (
            // Returnable items
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Description', 'Qty', 'Serial / Asset No.', ''].map(h => <th key={h} className="text-left px-3 py-2 text-xs font-medium text-gray-600">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {itemFields.map((field, idx) => (
                  <tr key={field.id}>
                    <td className="px-2 py-1"><input className={inputCls} placeholder="Item description" {...register(`items.${idx}.itemDescription`)} /></td>
                    <td className="px-2 py-1"><input type="number" className={`w-20 ${inputCls}`} {...register(`items.${idx}.quantity`)} /></td>
                    <td className="px-2 py-1"><input className={`w-32 ${inputCls}`} placeholder="S/N" {...register(`items.${idx}.serialNumber`)} /></td>
                    <td className="px-2 py-1">{itemFields.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : isExportShipment ? (
            // Export FP items — full detail
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Product Description *', 'Form *', 'Size *', 'Freezing *', 'Packing *', 'Cartons *', 'Brand', 'Net Wt (kg)', 'Gross Wt (kg)', ''].map(h =>
                    <th key={h} className="text-left px-2 py-2 text-xs font-medium text-gray-600 whitespace-nowrap">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {itemFields.map((field, idx) => {
                  const hasBrand = watch(`items.${idx}.brand`);
                  return (
                    <tr key={field.id}>
                      <td className="px-1 py-1"><input className={`w-36 ${inputCls}`} placeholder="Product description" {...register(`items.${idx}.productName`)} /></td>
                      <td className="px-1 py-1"><input className={`w-24 ${inputCls}`} placeholder="e.g. HOSO" {...register(`items.${idx}.form`)} /></td>
                      <td className="px-1 py-1"><input className={`w-20 ${inputCls}`} placeholder="e.g. 16/20" {...register(`items.${idx}.size`)} /></td>
                      <td className="px-1 py-1">
                        <select className={`w-28 ${selectCls}`} {...register(`items.${idx}.freezingType`)}>
                          <option value="">Select…</option>
                          {FREEZING_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1"><input className={`w-24 ${inputCls}`} placeholder="e.g. 2kg bag" {...register(`items.${idx}.packing`)} /></td>
                      <td className="px-1 py-1"><input type="number" className={`w-20 ${inputCls}`} {...register(`items.${idx}.cartons`)} /></td>
                      <td className="px-1 py-1 space-y-1">
                        <select className={`w-16 ${selectCls}`} {...register(`items.${idx}.brand`, { setValueAs: v => v === 'true' })}>
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                        {hasBrand && <input className={`w-24 ${inputCls} mt-1`} placeholder="Brand name" {...register(`items.${idx}.brandName`)} />}
                      </td>
                      <td className="px-1 py-1"><input type="number" step="0.01" className={`w-20 ${inputCls}`} {...register(`items.${idx}.netWeight`)} /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" className={`w-20 ${inputCls}`} {...register(`items.${idx}.grossWeight`)} /></td>
                      <td className="px-1 py-1">{itemFields.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : isFP ? (
            // Other FP items
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Product', 'Form', 'Size', 'Pack Size', 'Freezing', 'Packing', 'Cartons', 'Net Wt (kg)', 'Gross Wt (kg)', 'Lot No.', ''].map(h =>
                  <th key={h} className="text-left px-2 py-2 text-xs font-medium text-gray-600 whitespace-nowrap">{h}</th>
                )}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {itemFields.map((field, idx) => (
                  <tr key={field.id}>
                    <td className="px-1 py-1"><input className={`w-28 ${inputCls}`} {...register(`items.${idx}.productName`)} /></td>
                    <td className="px-1 py-1"><input className={`w-20 ${inputCls}`} {...register(`items.${idx}.form`)} /></td>
                    <td className="px-1 py-1"><input className={`w-20 ${inputCls}`} {...register(`items.${idx}.size`)} /></td>
                    <td className="px-1 py-1"><input className={`w-20 ${inputCls}`} {...register(`items.${idx}.packSize`)} /></td>
                    <td className="px-1 py-1">
                      <select className={`w-28 ${selectCls}`} {...register(`items.${idx}.freezingType`)}>
                        <option value="">—</option>
                        {FREEZING_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1"><input className={`w-20 ${inputCls}`} {...register(`items.${idx}.packing`)} /></td>
                    <td className="px-1 py-1"><input type="number" className={`w-16 ${inputCls}`} {...register(`items.${idx}.cartons`)} /></td>
                    <td className="px-1 py-1"><input type="number" step="0.01" className={`w-20 ${inputCls}`} {...register(`items.${idx}.netWeight`)} /></td>
                    <td className="px-1 py-1"><input type="number" step="0.01" className={`w-20 ${inputCls}`} {...register(`items.${idx}.grossWeight`)} /></td>
                    <td className="px-1 py-1"><input className={`w-24 ${inputCls} font-mono`} {...register(`items.${idx}.lotNumber`)} /></td>
                    <td className="px-1 py-1">{itemFields.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : isRM ? (
            // RM items (IN or OUT)
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Species', 'Grade', 'Form', 'Qty (kg)', 'Boxes',
                    ...(isInRM ? ['Supplier', 'Purchase Center', 'Lot No.', 'Temp (°C)'] : ['Lot No.']),
                    ''
                  ].map(h => <th key={h} className="text-left px-2 py-2 text-xs font-medium text-gray-600 whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {itemFields.map((field, idx) => (
                  <tr key={field.id}>
                    <td className="px-1 py-1"><input className={`w-28 ${inputCls}`} {...register(`items.${idx}.species`)} /></td>
                    <td className="px-1 py-1"><input className={`w-20 ${inputCls}`} placeholder="e.g. 16/20" {...register(`items.${idx}.grade`)} /></td>
                    <td className="px-1 py-1">
                      <select className={`w-24 ${selectCls}`} {...register(`items.${idx}.form`)}>
                        <option value="">—</option>
                        {FORMS.map(f => <option key={f}>{f}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1"><input type="number" step="0.01" className={`w-20 ${inputCls}`} {...register(`items.${idx}.quantity`)} /></td>
                    <td className="px-1 py-1"><input type="number" className={`w-16 ${inputCls}`} placeholder="Boxes" {...register(`items.${idx}.boxes`)} /></td>
                    {isInRM && <>
                      <td className="px-1 py-1">
                        <select className={`w-36 ${selectCls}`} {...register(`items.${idx}.supplierId`)}>
                          <option value="">Select supplier…</option>
                          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <select className={`w-36 ${selectCls}`} {...register(`items.${idx}.purchaseCenterId`)}>
                          <option value="">Select center…</option>
                          {purchaseCenters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </td>
                    </>}
                    <td className="px-1 py-1">
                      {isInRM && watchItems[idx]?.lotNumber
                        ? <span className="font-mono text-xs text-teal-700 font-bold px-2">{watchItems[idx].lotNumber}</span>
                        : !isInRM && <input className={`w-24 ${inputCls} font-mono`} {...register(`items.${idx}.lotNumber`)} />
                      }
                      {isInRM && !watchItems[idx]?.lotNumber && <span className="text-xs text-gray-400 px-2">auto</span>}
                    </td>
                    {isInRM && <td className="px-1 py-1"><input type="number" step="0.1" className={`w-20 ${inputCls}`} {...register(`items.${idx}.temperature`)} /></td>}
                    <td className="px-1 py-1">{itemFields.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      </Card>

      {/* ── Remarks ───────────────────────────────────────────────── */}
      <Card title="Remarks">
        <div className="p-6">
          <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" rows={3} placeholder="Optional remarks…" {...register('remarks')} />
        </div>
      </Card>

      <div className="flex justify-end gap-3 pb-6">
        <Link to="/gate-passes"><Button variant="secondary" type="button">Cancel</Button></Link>
        <Button type="submit" loading={isSubmitting}>{isEdit ? 'Save Changes' : 'Create Gate Pass'}</Button>
      </div>

      {/* ── Inline Customer Modal ────────────────────────────────── */}
      <InlineCustomerModal
        open={inlineCustomerOpen}
        onClose={() => setInlineCustomerOpen(false)}
        onCreate={handleInlineCustomerCreate}
      />
    </form>
  );
}
